import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppFields } from "../components/apps/AppFields";
import { PageHeader } from "../components/shell/PageHeader";
import { Button } from "../components/ui/Button";
import { EmptyState, ErrorNotice, SkeletonRows } from "../components/ui/Feedback";
import { Pill } from "../components/ui/Status";
import { Cell, TableFrame, TableHead, TableRow } from "../components/ui/Table";
import type { Column } from "../components/ui/Table";
import { Metric } from "../components/ui/Text";
import { getPage, post } from "../lib/api";
import { count } from "../lib/format";
import { useSession } from "../lib/session";
import type { AppDetail, AppSummary } from "../lib/types";

/**
 * Apps — the third parties allowed to read data through `/openapi/v1`.
 *
 * The list answers "who can get in, and is anyone hammering us today?": an app's
 * grants, how many live keys it has, and today's answered and refused calls.
 * Today is the quota day, which starts at midnight in Phnom Penh whatever the
 * browser's zone, because that is where the day and month limits reset.
 *
 * Everything else — keys, grants, limits, history — is on the app's own page.
 */

const COLUMNS: readonly Column[] = [
  { key: "name", label: "App", width: "24%" },
  { key: "holder", label: "Acts for", width: "16%" },
  { key: "permissions", label: "May read", width: "22%" },
  { key: "keys", label: "Live keys", numeric: true, width: "10%" },
  { key: "today", label: "Calls today", numeric: true, width: "11%" },
  { key: "refused", label: "Refused today", numeric: true, width: "11%" },
  { key: "state", label: "" },
];

export function Apps() {
  const { can } = useSession();
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);

  const apps = useQuery({
    queryKey: ["apps"],
    queryFn: () => getPage<AppSummary>("/apps"),
  });

  const rows = apps.data?.rows ?? [];
  const live = rows.filter((row) => !row.disabled).length;
  const calls = rows.reduce((sum, row) => sum + row.todayAllowed, 0);
  const refused = rows.reduce((sum, row) => sum + row.todayDenied, 0);

  const createButton =
    can("apps.create") && !isCreating ? (
      <Button variant="primary" onClick={() => setIsCreating(true)}>
        New app
      </Button>
    ) : undefined;

  return (
    <>
      <PageHeader
        eyebrow="Configuration"
        title="Apps"
        metrics={
          <>
            <Metric label="Enabled" value={count(live)} hint={`of ${count(rows.length)}`} />
            <Metric label="Calls today" value={count(calls)} hint="quota day, Phnom Penh" />
            <Metric
              label="Refused today"
              value={count(refused)}
              hint="over a rate limit"
              tone={refused > 0 ? "signal" : "muted"}
            />
          </>
        }
        lede="Partners that read cards, VINs and charging sessions through the external API. Each app sees only what it is granted, for the group it acts for, at the rates set on its page, with keys you issue and can revoke."
        actions={createButton}
      />

      {isCreating ? (
        <AppFields
          heading="New app"
          submitLabel="Create app"
          submit={(body) => post<AppDetail>("/apps", body)}
          onDone={(app) => navigate(`/apps/${app.id}`)}
          onCancel={() => setIsCreating(false)}
        />
      ) : null}

      {apps.isError ? <ErrorNotice error={apps.error} onRetry={() => void apps.refetch()} /> : null}

      <TableFrame>
        <TableHead columns={COLUMNS} />
        {apps.isPending ? (
          <SkeletonRows rows={3} columns={COLUMNS.length} />
        ) : (
          <tbody>
            {rows.map((row, index) => (
              <TableRow
                key={row.id}
                index={index}
                onActivate={() => navigate(`/apps/${row.id}`)}
                isFaulted={row.todayDenied > 0 && !row.disabled}
              >
                <Cell>
                  <span className="font-medium text-ink">{row.name}</span>
                  {row.description === null ? null : (
                    <span className="block truncate text-micro tracking-normal normal-case text-ink-faint">
                      {row.description}
                    </span>
                  )}
                </Cell>
                <Cell muted={row.holder === null}>{row.holder?.name ?? "Every holder"}</Cell>
                <Cell muted={row.permissions.length === 0} className="font-mono text-micro">
                  {row.permissions.length === 0 ? "nothing yet" : row.permissions.join(", ")}
                </Cell>
                <Cell numeric muted={row.activeKeys === 0}>
                  {count(row.activeKeys)}
                </Cell>
                <Cell numeric>{count(row.todayAllowed)}</Cell>
                <Cell numeric muted={row.todayDenied === 0}>
                  {count(row.todayDenied)}
                </Cell>
                <Cell>{row.disabled ? <Pill tone="faulted">Disabled</Pill> : null}</Cell>
              </TableRow>
            ))}
          </tbody>
        )}
      </TableFrame>

      {!apps.isPending && !apps.isError && rows.length === 0 ? (
        <EmptyState
          title="No apps yet."
          detail="An app is a partner's way in: create one, grant it what it may read, then issue it a key."
          action={createButton}
        />
      ) : null}
    </>
  );
}
